import {
    ElementType,
    ISelectElement,
    IGridLineElement as ISelectGridLine,
    IAxisElement as ISelectAxis,
    IGeometryElement as ISelectGeometry,
    IDataLabelElement as ISelectDataLabel,
    BorderType,
} from '@tencent/chart/lib/chart-ui/interface';
import type { Chart as G2Chart } from '@tencent/chart/lib/antv/g2';
import {
    Axis,
    AxisElementDirection,
    IAxisElement,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/axis';
import { Legend } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/legend';
import { DataLabel } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/data-label';
import { Geometry } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/geometry';
import {
    ChartElementType,
    IChartElement,
    IChartElementGroup,
    SelectStyleType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';

const selectTypeMap: Record<BorderType, SelectStyleType> = {
    [BorderType.ANCHOR]: SelectStyleType.ANCHOR,
    [BorderType.EDGE]: SelectStyleType.ENDS_ANCHOR,
    [BorderType.RECT]: SelectStyleType.BORDER,
};

export class ChartElementManager {
    public axis: Axis;
    public legend: Legend;
    public dataLabel: DataLabel;
    public geometry: Geometry;

    constructor() {
        this.axis = new Axis();
        this.legend = new Legend();
        this.dataLabel = new DataLabel();
        this.geometry = new Geometry();
    }

    // TODO 后续getChartElements所有相关逻辑移除
    public getChartElements(chart: G2Chart): Array<IChartElement | IChartElementGroup> {
        const axisElements = this.axis.getElements(chart);
        const legendElements = this.legend.getElements(chart);
        const dataLabelElements = this.dataLabel.getElements(chart);
        const geometryElements = this.geometry.getElements(chart);

        // 下面的顺序是层级顺序，在时间点击中只会点到层级高的元素。
        return [
            ...axisElements,
            ...geometryElements,
            ...legendElements,
            ...dataLabelElements,
        ];
    }

    public convertToChartElement(ele: ISelectElement): IChartElement {
        // TODO 把之前extra中的一些字段设置上去，还有一些比如坐标轴的axisId，也要通过field position来获取axId并设置
        const type = ele.elementType as unknown as ChartElementType;
        const rect = {
            ...ele.rect,
            left: ele.rect?.x || 0,
            top: ele.rect?.y || 0,
        };

        const chartElement: IChartElement = {
            id: ele.id,
            groupId: ele.groupId,
            type,
            rect: rect as IChartElement['rect'],
            path: ele.path as Array<Array<string|number>>,
            shapeType: ele.shapeType,
            selectStyle: selectTypeMap[ele.borderType],
        };

        if (this.isGridLineElement(ele)) {
            chartElement.type = ele.gridType === 'subGrid' ? ChartElementType.MINOR_GRID : ChartElementType.MAJOR_GRID;
        }

        if (this.isAxisElement(ele)) {
            (chartElement as IAxisElement).direction = ele.position as AxisElementDirection;
            chartElement.extra = {
                axisId: ele.extra.axisId,
            };
        }

        if (this.isGeometryElement(ele) || this.isDataLabelElement(ele)) {
            const data = Array.isArray(ele.data) ? ele.data[0] : ele.data;
            const { seriesId, dataId, specialGeometryType, chartId } = data;
            chartElement.extra = {
                seriesId,
                dataId,
                specialGeometryType,
                chartId,
                data: ele.data,
            };
        }

        return chartElement;
    }

    private isGridLineElement(element: ISelectElement): element is ISelectGridLine {
        return element.elementType === ElementType.GRID;
    }

    private isAxisElement(element: ISelectElement): element is ISelectAxis {
        return element.elementType === ElementType.AXIS_LABEL
            || element.elementType === ElementType.AXIS_TITLE
            || element.elementType === ElementType.AXIS_TICK
            || element.elementType === ElementType.GRID;
    }

    private isGeometryElement(element: ISelectElement): element is ISelectGeometry {
        return element.elementType === ElementType.GEOMETRY;
    }

    private isDataLabelElement(element: ISelectElement): element is ISelectDataLabel {
        return element.elementType === ElementType.DATA_LABEL;
    }
}
